import React, { useState } from 'react';
import { Check, Tag, Gift, Sparkles, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/components/ui/use-toast';
import { BASE_URL } from '@/config/api';

interface PromoCodeModalProps {
  onClose: () => void;
  token: string;
  reloadUser: () => void;
}

const PromoCodeModal: React.FC<PromoCodeModalProps> = ({ onClose, token, reloadUser }) => {
  const [promoCode, setPromoCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useLanguage();
  const { toast } = useToast();

  const checkPromoCode = async () => {
    if (!promoCode.trim()) return;

    setIsLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/stripe/apply/code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ coupon_id: promoCode }),
      });

      const data = await response.json();

      if (response.ok) {
        if (!data.isValid) {
          toast({
            title: t('subscription.invalid_promo_code'),
            variant: 'destructive',
          });
          setIsLoading(false);
        } else {
          toast({
            title: t('subscription.promo_code_applied'),
            variant: 'default',
          });
          setIsSuccess(true);
          setTimeout(() => {
            reloadUser();
            onClose();
          }, 2200);
        }
      } else {
        throw new Error(data.error || t('subscription.error_checking_promo_code'));
      }
    } catch (error) {
      console.error('Failed to check promo code:', error);
      toast({
        title: t('subscription.error_checking_promo_code'),
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Header avec gradient */}
      <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 p-6 text-white rounded-t-xl">
        <div className="absolute inset-0 bg-black/10 rounded-t-xl"></div>
        <div className="relative flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
            <Gift className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{t('subscription.promo_code')}</h2>
            <p className="text-white/80 text-sm">{t('subscription.addYourCouponCode')}</p>
          </div>
        </div>

        {/* Particules décoratives */}
        <div className="absolute top-2 right-4">
          <Sparkles className="h-4 w-4 text-white/60" />
        </div>
        <div className="absolute bottom-3 right-8">
          <Sparkles className="h-3 w-3 text-white/40" />
        </div>
      </div>

      {/* Contenu */}
      <div className="p-6 bg-white dark:bg-gray-900 rounded-b-xl">
        {isSuccess ? (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('subscription.promo_code_applied')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t('subscription.discountWillBeApply')}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('subscription.promo_code')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Tag className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && checkPromoCode()}
                  placeholder={t('subscription.enterCode')}
                  className="block w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Info card */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                    {t('subscription.importantInfo')}
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    {t('subscription.discountWillBeApply')}
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
                disabled={isLoading}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={checkPromoCode}
                disabled={!promoCode.trim() || isLoading}
                className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-400 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('subscription.apply')
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoCodeModal;
